import type { CityConfig } from "../city-template/types";

export const windhamNhConfig: CityConfig = {
  city: "Windham",
  state: "New Hampshire",
  stateAbbr: "NH",
  slug: "windham-nh",
  route: "/cities/windham-nh",
  title: "Emergency Passport Windham, NH | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Windham, NH — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Rockingham County travelers near the Massachusetts border. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Windham, NH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Windham and southern New Hampshire travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Windham%20New%20Hampshire%20scenic%20suburban%20landscape%20with%20beautiful%20New%20England%20homes%2C%20the%20Searles%20Castle%20and%20Castle%20Spring%20Road%20area%2C%20lush%20green%20rolling%20hills%20and%20mature%20trees%2C%20peaceful%20residential%20neighborhood%20with%20large%20lots%2C%20warm%20golden%20afternoon%20light%2C%20autumn%20foliage%20with%20red%20and%20orange%20leaves%2C%20clean%20and%20inviting%20suburban%20atmosphere%2C%20no%20visible%20text%20or%20people%2C%20photorealistic%20landscape%20with%20warm%20earth%20tones%20and%20green%20meadows%2C%20picturesque%20southern%20New%20Hampshire%20scenery&width=1800&height=700&seq=windham-nh-hero-001&orientation=landscape",
  heroImageAlt: "Windham New Hampshire scenic suburban landscape with beautiful New England homes and rolling hills",
  geoPosition: "42.8004;-71.3042",
  geoPlacename: "Windham, New Hampshire",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "10 Causeway St Suite 247, Boston, MA 02222",
    distance: "Located in downtown Boston — approximately 40–55 minutes from Windham via I-93",
  },
  nearbyCitiesZips: [
    { name: "Windham", zips: "03087" },
    { name: "Derry", zips: "03038" },
    { name: "Salem", zips: "03079" },
    { name: "Pelham", zips: "03076" },
    { name: "Hudson", zips: "03051" },
    { name: "Londonderry", zips: "03053" },
    { name: "Nashua", zips: "03060–03064" },
    { name: "Merrimack", zips: "03054" },
    { name: "Atkinson", zips: "03811" },
    { name: "Hampstead", zips: "03841" },
    { name: "Chester", zips: "03036" },
    { name: "Auburn", zips: "03032" },
    { name: "Sandown", zips: "03873" },
    { name: "Danville", zips: "03819" },
    { name: "East Derry", zips: "03041" },
    { name: "Litchfield", zips: "03052" },
    { name: "Manchester", zips: "03101–03111" },
    { name: "Bedford", zips: "03110" },
    { name: "Goffstown", zips: "03045" },
    { name: "Hampton", zips: "03842" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent southern New Hampshire departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Derry", route: "/cities/derry" },
    { name: "Salem", route: "/cities/salem" },
    { name: "Londonderry", route: "/cities/londonderry" },
    { name: "Nashua", route: "/cities/nashua" },
    { name: "Merrimack", route: "/cities/merrimack" },
    { name: "Hudson", route: "/cities/hudson" },
    { name: "Hampton", route: "/cities/hampton" },
    { name: "Manchester", route: "/manchester" },
  ],
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeNote: "Fast Passport Center serves Windham remotely via secure courier. We do not have a walk-in office in Windham — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for southern New Hampshire travelers.",
  fpcOfficeDistance: "Serving all of Rockingham and Hillsborough Counties",
  serviceAreaIntro: "From the beautiful Searles Castle area to the Cobbetts Pond shoreline, from the Windham town center to the eastern rural corridors — Fast Passport Center serves the entire Windham community. We regularly help travelers from Derry, Salem, Pelham, and Hudson get their passports fast.",
  nearbyCitiesIntro: "We serve all Windham neighborhoods and nearby southern New Hampshire cities including Derry, Salem, Pelham, Hudson, Londonderry, Nashua, Merrimack, Atkinson, Hampstead, Chester, Auburn, Sandown, and Manchester. If you are in the greater Windham area, we have you covered.",
  howItWorksIntro: "Most Windham travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency.",
  mistakesIntro: "With Manchester-Boston Regional Airport (MHT) just 20 minutes away and Boston Logan only 45 minutes away, Windham travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Windham and southern New Hampshire travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Windham? Here are the most common ones we hear from southern New Hampshire travelers.",
  statePageRoute: "/states/new-hampshire",
  statePageName: "New Hampshire",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Windham international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Christine M.",
      location: "Windham, NH",
      rating: 5,
      text: "My family was flying to Italy in 4 days and my husband's passport had expired. Fast Passport Center got his renewal processed in 36 hours. We made our trip and created memories for a lifetime.",
      service: "Passport Renewal",
    },
    {
      name: "Paul R.",
      location: "Salem, NH",
      rating: 5,
      text: "As a business consultant who travels internationally from the Boston area, I use Fast Passport Center for all my renewals. They've never let me down. Fast and professional every time.",
      service: "Expedited Renewal",
    },
    {
      name: "Amanda S.",
      location: "Derry, NH",
      rating: 5,
      text: "Lost my passport two days before a work trip to Montreal. Called them in a panic and they had a replacement in my hands within 48 hours. Truly a lifesaver.",
      service: "Lost Passport",
    },
    {
      name: "Kevin B.",
      location: "Londonderry, NH",
      rating: 5,
      text: "The team reviewed my application and caught a signature error that would have delayed everything. That level of attention is why I recommend them to everyone in southern NH.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "windham-nh-1",
      name: "Windham Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7 Fellows Rd",
      city: "Windham",
      state: "NH",
      zip: "03087",
      phone: "(603) 893-2395",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.5 miles from downtown Windham",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Windham. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "windham-nh-2",
      name: "Salem Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 Main St",
      city: "Salem",
      state: "NH",
      zip: "03079",
      phone: "(603) 893-2921",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4 miles from Windham",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Large facility with extended hours. Convenient for southern Rockingham County residents. Photo services available. Book appointments well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "windham-nh-3",
      name: "Derry Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 W Broadway",
      city: "Derry",
      state: "NH",
      zip: "03038",
      phone: "(603) 432-1155",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles from Windham",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for central Rockingham County residents. Limited hours — book appointments early.",
      icon: "ri-mail-line",
    },
    {
      id: "windham-nh-4",
      name: "Hudson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "40 Derry St",
      city: "Hudson",
      state: "NH",
      zip: "03051",
      phone: "(603) 883-3981",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "6 miles from Windham",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for western Rockingham County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "windham-nh-5",
      name: "Londonderry Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1A Commons Dr",
      city: "Londonderry",
      state: "NH",
      zip: "03053",
      phone: "(603) 432-4215",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "3 miles from Windham",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves eastern Rockingham County. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "windham-nh-6",
      name: "Nashua City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "229 Main St",
      city: "Nashua",
      state: "NH",
      zip: "03060",
      phone: "(603) 589-3010",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "12 miles from Windham",
      website: "https://www.nashuanh.gov/",
      websiteLabel: "Nashua City Clerk",
      notes: "Major municipal facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Windham photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Rockingham County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Windham applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Windham families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Windham travelers often assume standard processing will be fine. It takes 6–13 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Windham or Salem." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Windham address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Windham before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Windham for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most southern New Hampshire travelers." },
    { q: "How fast can I renew my passport in Windham?", a: "Fast Passport Center offers fast passport renewal in Windham in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Windham in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Windham?", a: "Same-day passport appointments are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Windham residents." },
    { q: "Can I expedite a child passport in Windham?", a: "Yes. Fast Passport Center expedites child passports in Windham for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Windham with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Windham?", a: "With Fast Passport Center's emergency courier service, Windham travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to visit the Boston Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Windham?", a: "Popular options include the Windham Post Office (03087), Salem Post Office (03079), Derry Post Office (03038), Hudson Post Office (03051), and Nashua City Clerk (03060). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Windham?", a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in a nearby town like Derry or Salem?", a: "We serve all of southern New Hampshire including Derry, Salem, Pelham, Hudson, Londonderry, Nashua, Merrimack, Atkinson, Hampstead, and Chester. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Windham?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Salem", address: "60 Main St, Salem, NH 03079", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Salem", address: "55 Main St, Salem, NH 03079", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Nashua", address: "77 Main St, Nashua, NH 03060", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Derry", address: "20 W Broadway, Derry, NH 03038", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Salem", address: "254 N Broadway, Salem, NH 03079", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Derry", address: "42 W Broadway, Derry, NH 03038", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Nashua", address: "10 Main St, Nashua, NH 03060", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Nashua", address: "71 Front St, Nashua, NH 03060", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    { name: "DMV — Salem", address: "23 Main St, Salem, NH 03079", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID and driver's license renewals. Passport-ready photo services available. Online appointment booking.", featured: true },
    { name: "DMV — Nashua", address: "33 Main St, Nashua, NH 03060", hours: "Mon–Fri: 8AM–5PM", note: "Passport-ready photo services available. Appointment required.", featured: false },
    { name: "DMV — Manchester", address: "23 Hazard Ave, Manchester, NH 03109", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID processing. Walk-ins limited — book online first.", featured: false },
    { name: "DMV — Derry", address: "23 W Broadway, Derry, NH 03038", hours: "Mon–Fri: 8AM–5PM", note: "Full-service center for Rockingham County residents.", featured: true },
  ],
  airports: [
    { code: "MHT", name: "Manchester-Boston Regional Airport", distance: "20 miles from Windham", address: "1 Airport Rd, Manchester, NH 03103", note: "Closest major commercial airport for Windham travelers. International flights to Canada and Europe. Major domestic hub.", flightUrl: "https://www.flymanchester.com/", flightLabel: "MHT Flight Info", featured: true },
    { code: "BOS", name: "Boston Logan International Airport", distance: "45 miles from Windham", address: "1 Harborside Dr, Boston, MA 02128", note: "Major international hub for long-haul flights to Europe, Asia, and South America. Accessible via I-93 or Route 3.", flightUrl: "https://www.massport.com/logan-airport", flightLabel: "Logan Airport", featured: false },
    { code: "PSM", name: "Portsmouth International Airport", distance: "40 miles from Windham", address: "50 Portsmouth Ave, Portsmouth, NH 03801", note: "Regional airport with Allegiant flights to Florida and seasonal destinations.", flightUrl: "https://www.flypsm.com/", flightLabel: "PSM Flight Info", featured: false },
  ],
};